<template>
  <div :class="`iconn_button ${fullWidth ? 'full_width' : ''}`">
    <VBtn :id="id" style="letter-spacing: 0" :class="`button button_${size} button_${type} ${fullWidth ? 'full_width' : ''}`"
      :disabled="disabled" :ripple="false" :loading="loading" @click="$emit('onClick')" v-on:click.stop>
      <!-- Left icon -->
      <Icon v-if="leftIcon && useMaterial"
        class="margin-right_xs-df"
        :style="{ marginBottom: bottomMargin }"
        :icon="leftIcon"
        :size="iconSize"
        :variant="iconVariant"
        :color="customColorIcon ? customColorIcon
         : (type === 'primary' && !disabled ? 'white' : disabled ? '' : type === 'secondary_flat' ? '#64748B' : type)"
      />
      <SvgFactory v-if="leftIcon && !useMaterial" class="margin-right_xs-df" :style="{ marginBottom: bottomMargin }"
        :width="iconSize"
        :name="leftIcon"
        :color="customColorIcon ? ''
         : (type === 'primary' && !disabled ? 'white' : disabled ? '' : type === 'secondary_flat' ? '#64748B' : type)"
        :customColor="customColorIcon"
      />
      <!-- Button text -->
      <p>{{ text }}</p>

      <!-- Right icon -->
      <Icon v-if="rightIcon && useMaterial"
        class="margin-left_xs-df"
        :style="{ marginBottom: bottomMargin }"
        :icon="rightIcon"
        :size="iconSize"
        :color="customColorIcon ? customColorIcon
         : (type === 'primary' && !disabled ? 'white' : disabled ? '' : type === 'secondary_flat' ? '#64748B' : type)"
      />
      <SvgFactory v-if="rightIcon && !useMaterial" class="margin-left_xs-df" :style="{ marginBottom: bottomMargin }"
        :width="iconSize"
        :name="rightIcon"
        :color="customColorIcon ? ''
         : (type === 'primary' && !disabled ? 'white' : disabled ? '' : type === 'secondary_flat' ? '#64748B' : type)"
        :customColor="customColorIcon"
      />
    </VBtn>
  </div>
</template>

<script>
import SvgFactory from '@design_system_v2/atoms/SvgFactory.vue';
import Icon from '@design_system_v2/atoms/Icon.vue';

export default {
  name: 'Button',
  components: {
    SvgFactory,
    Icon,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    leftIcon: {
      type: String,
      default: '',
    },
    rightIcon: {
      type: String,
      default: '',
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
    },
    sizeIcon: {
      type: Number,
      default: null,
    },
    type: {
      type: String,
      default: 'primary',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    colorIcon: {
      type: String,
      default: '',
    },
    iconVariant: {
      type: String,
      default: '',
    },
    useMaterial: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onClick'],
  methods: {
    onClick() {
      this.$emit('onClick');
    },
  },
  computed: {
    iconSize() {
      if (this.sizeIcon) {
        return this.sizeIcon;
      }
      switch (this.size) {
        case 'sm':
          return 16;
        case 'md':
          return 16;
        case 'lg':
          return 20;
        default:
          return 16;
      }
    },
    customColorIcon() {
      if (this.colorIcon) {
        return this.colorIcon;
      }
    },
    bottomMargin() {
      if (this.iconNeedsBottomMargin.includes(this.rightIcon)) {
        return '12px';
      } if (this.iconNeedsBottomMargin.includes(this.leftIcon)) {
        return '2.5px';
      }
      return 0;
    },
  },
  data() {
    return {
      iconNeedsBottomMargin: ['icon_right', 'icon_iconnections', 'icon_save'],
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@foundation";

.full_width {
  width: 100%;
}

.button {
  padding-left: 24px !important;
  padding-right: 24px !important;

  // Size classes
  &_sm {
    @extend .height_lg-important;
    @extend .medium-body-sm;
  }

  &_md {
    @extend .height_xl-important;
    @extend .medium-body-sm;
  }

  &_lg {
    @extend .height_2xl-important;
    width: 210px;
    @extend .medium-body-md;
  }

  // Type classes
  &_primary {
    @extend .typography_white;
    @extend .gray-shadow-xs;
    background-color: map-get($primary, "purple-500") !important;

    &:hover {
      background-color: map-get($primary, "purple-300") !important;
    }

    &:active {
      background-color: map-get($primary, "purple-700") !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($grey_scale, "grey-200") !important;
    }
  }
  &_primary_flat {
    @extend .typography_primary;
    @extend .border_stroke_sm_df;
    @extend .border_color_neutral_default;
    box-shadow: none;

    &:hover {
      @extend .border_color_purple_default;
    }

    &:active {
      @extend .border_stroke_md;
      @extend .border_color_purple_default;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($grey_scale, "grey-200") !important;
    }
  }

  &_secondary {
    @extend .typography_default;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    border-color: map-get($primary, "purple-500");

    &:hover {
      @extend .border_stroke_md;
      border-color: map-get($primary, "purple-500");
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($neutral, "neutral-900");
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, "white");
      border-color: map-get($neutral, "neutral-700");
    }
  }

  &_secondary_flat {
    @extend .typography_default;
    @extend .border_stroke_sm_df;
    border-color: map-get($primary, "purple-500");
    box-shadow: none;

    &:hover {
      border: 2px solid !important;
      border-color: map-get($primary, "purple-600") !important;
    }

    &:active {
      border: 2px solid !important;
      border-color: map-get($primary, "purple-600") !important;
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, "white");
      border-color: map-get($neutral, "neutral-700");
    }

  }

  &_success-outline {
    @extend .typography_success;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    border-color: map-get($success, "green-800");

    &:hover {
      @extend .border_stroke_md;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($success, "green-900");
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, "white");
      border-color: map-get($neutral, "neutral-700");
    }
  }

  &_success-filled {
    @extend .typography_white;
    @extend .gray-shadow-xs;
    background: map-get($success, "green-700");

    &:hover {
      background-color: map-get($success, "green-600");
    }

    &:active {
      background-color: map-get($success, "green-900");
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($grey_scale, "grey-200");
    }
  }

  &_text {
    @extend .typography_info;
    border: none;
    background-color: transparent;
    box-shadow: none;

    &:hover {
      font-weight: 700;
      color: map-get($secondary, 'blue-700')
    }

    &:active {
      font-weight: 700;
      color: map-get($secondary, 'blue-900')
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($grey_scale, "grey-200");
    }
  }

  &_warning-outline {
    @extend .typography_warning;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    border-color: map-get($warning, "orange-500");

    &:hover {
      @extend .border_stroke_md;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($warning, "orange-700");
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, "white");
      border-color: map-get($neutral, "neutral-700");
    }
  }

  &_info-outline {
    @extend .typography_info;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    border-color: map-get($secondary, "blue-600");

    &:hover {
      @extend .border_stroke_md;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($secondary, "blue-800");
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, "white");
      border-color: map-get($neutral, "neutral-700");
    }
  }

  &_error-outline {
    @extend .typography_error;
    @extend .border_stroke_sm_df;
    @extend .gray-shadow-xs;
    border-color: map-get($error, "red-500");

    &:hover {
      @extend .border_stroke_md;
    }

    &:active {
      @extend .border_stroke_md;
      border-color: map-get($error, "red-700");
    }

    &:disabled {
      @extend .typography_disabled;
      background-color: map-get($basic, "white");
      border-color: map-get($neutral, "neutral-700");
    }
  }
}
</style>

<style lang="scss">
.iconn_button {
  .v-btn {
    text-transform: none;
  }

  .v-btn__overlay {
    background-color: unset;
  }
}
</style>
